import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getStatusLabel as getCentralStatusLabel } from '../constants/statuses';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ChevronLeft, ChevronRight, Calendar, Truck, MapPin, 
  Loader2, RefreshCw, List, CalendarDays, Building2, Route
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Couleurs VIVES par statut
const STATUS_COLORS = {
  planned: { 
    bg: 'bg-slate-500', 
    bgLight: 'bg-slate-100',
    border: 'border-slate-600', 
    text: 'text-white',
    textLight: 'text-slate-700',
    dot: 'bg-slate-500'
  },
  planifiee: { 
    bg: 'bg-slate-500', 
    bgLight: 'bg-slate-100',
    border: 'border-slate-600', 
    text: 'text-white',
    textLight: 'text-slate-700',
    dot: 'bg-slate-500'
  },
  en_cours_de_chargement: { 
    bg: 'bg-blue-600', 
    bgLight: 'bg-blue-100',
    border: 'border-blue-700', 
    text: 'text-white',
    textLight: 'text-blue-700',
    dot: 'bg-blue-600'
  },
  en_transit: { 
    bg: 'bg-amber-500', 
    bgLight: 'bg-amber-100',
    border: 'border-amber-600', 
    text: 'text-white',
    textLight: 'text-amber-700',
    dot: 'bg-amber-500'
  },
  en_cours_de_dechargement: { 
    bg: 'bg-purple-600', 
    bgLight: 'bg-purple-100',
    border: 'border-purple-700', 
    text: 'text-white',
    textLight: 'text-purple-700',
    dot: 'bg-purple-600'
  },
  in_progress: { 
    bg: 'bg-orange-500', 
    bgLight: 'bg-orange-100',
    border: 'border-orange-600', 
    text: 'text-white',
    textLight: 'text-orange-700',
    dot: 'bg-orange-500'
  },
  completed: { 
    bg: 'bg-emerald-600', 
    bgLight: 'bg-emerald-100',
    border: 'border-emerald-700', 
    text: 'text-white',
    textLight: 'text-emerald-700',
    dot: 'bg-emerald-600'
  },
  cancelled: { 
    bg: 'bg-red-600', 
    bgLight: 'bg-red-100',
    border: 'border-red-700', 
    text: 'text-white',
    textLight: 'text-red-700',
    dot: 'bg-red-600'
  }
};

const STATUS_LABELS = {
  planned: 'Planifié',
  planifiee: 'Planifié',
  en_cours_de_chargement: 'Chargement',
  en_transit: 'En transit',
  en_cours_de_dechargement: 'Déchargement',
  in_progress: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé'
};

// Composant pour un événement dans le calendrier
const CalendarEvent = ({ event, onClick, compact = false }) => {
  const colors = STATUS_COLORS[event.status] || STATUS_COLORS.planned;
  const trips = event.numberOfTrips || 1;
  
  // Extraire les infos
  const clientName = event.clientName || event.client || '';
  const departure = event.departure?.split(',')[0] || '';
  const arrival = event.arrival?.split(',')[0] || '';
  const shortRoute = departure && arrival ? `${departure} → ${arrival}` : '';
  
  if (compact) {
    return (
      <div
        className={cn(
          "text-xs px-2 py-1 rounded-md cursor-pointer font-medium shadow-sm hover:shadow-md transition-all",
          colors.bg, colors.text
        )}
        onClick={onClick}
        title={`${clientName}\n${shortRoute}\nStatut: ${STATUS_LABELS[event.status] || getCentralStatusLabel(event.status)}${trips > 1 ? `\nVoyages: x${trips}` : ''}`}
      >
        <div className="truncate flex items-center gap-1">
          {clientName || event.title?.split('-').pop()}
          {trips > 1 && <span className="bg-white/30 rounded px-1 text-[10px]">x{trips}</span>}
        </div>
      </div>
    );
  }
  
  return (
    <div
      className={cn(
        "text-xs px-2 py-1.5 rounded-md cursor-pointer shadow-sm hover:shadow-md transition-all border-l-4",
        colors.bgLight, colors.textLight, `border-l-${colors.dot.replace('bg-', '')}`
      )}
      style={{ borderLeftColor: colors.bg.includes('slate') ? '#64748b' : 
               colors.bg.includes('blue') ? '#2563eb' :
               colors.bg.includes('amber') ? '#f59e0b' :
               colors.bg.includes('purple') ? '#9333ea' :
               colors.bg.includes('orange') ? '#f97316' :
               colors.bg.includes('emerald') ? '#059669' :
               colors.bg.includes('red') ? '#dc2626' : '#64748b' }}
      onClick={onClick}
      title={`${clientName}\n${shortRoute}\nStatut: ${STATUS_LABELS[event.status] || getCentralStatusLabel(event.status)}${trips > 1 ? `\nVoyages: x${trips}` : ''}`}
    >
      <div className="font-semibold truncate flex items-center gap-1">
        <Building2 className="w-3 h-3 flex-shrink-0" />
        {clientName || 'N/A'}
        {trips > 1 && <span className="bg-black/10 rounded px-1 text-[10px] font-bold">x{trips}</span>}
      </div>
      {shortRoute && (
        <div className="text-[10px] opacity-80 truncate flex items-center gap-1 mt-0.5">
          <Route className="w-2.5 h-2.5 flex-shrink-0" />
          {shortRoute}
        </div>
      )}
    </div>
  );
};

const TransferCalendar = ({ contractId = null }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDay, setSelectedDay] = useState(null);

  // Get date range for current view
  const getDateRange = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (view === 'month') {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      return {
        start: firstDay.toISOString().split('T')[0],
        end: lastDay.toISOString().split('T')[0]
      };
    } else {
      const dayOfWeek = currentDate.getDay();
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - dayOfWeek + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return {
        start: startOfWeek.toISOString().split('T')[0],
        end: endOfWeek.toISOString().split('T')[0]
      };
    }
  }, [currentDate, view]);

  // Fetch calendar events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      let url = `${API}/contract-runs/calendar/view?start_date=${start}&end_date=${end}`;
      
      if (contractId) {
        url += `&contract_id=${contractId}`;
      }
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      
      const response = await axios.get(url);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      toast.error('Erreur lors du chargement du calendrier');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, contractId, statusFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Navigation
  const goToPrevious = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (view === 'month') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setDate(prev.getDate() - 7);
      }
      return newDate;
    });
  };

  const goToNext = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (view === 'month') {
        newDate.setMonth(prev.getMonth() + 1);
      } else {
        newDate.setDate(prev.getDate() + 7);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;
    
    const days = [];
    
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  // Get events for a specific day
  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.start === dateStr);
  };

  // Format date for display
  const formatMonthYear = () => {
    return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const handleEventClick = (event) => {
    navigate(`/admin/transfers/${event.id}`);
  };

  const days = generateCalendarDays();
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-red-500" />
            Calendrier des Transferts
          </CardTitle>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="planifiee">Planifiés</SelectItem>
                <SelectItem value="en_cours_de_chargement">Chargement</SelectItem>
                <SelectItem value="en_transit">En transit</SelectItem>
                <SelectItem value="en_cours_de_dechargement">Déchargement</SelectItem>
                <SelectItem value="completed">Terminés</SelectItem>
                <SelectItem value="cancelled">Annulés</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Refresh */}
            <Button variant="outline" size="icon" onClick={fetchEvents}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToPrevious}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Aujourd'hui
            </Button>
            <Button variant="outline" size="sm" onClick={goToNext}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <h3 className="text-xl font-bold capitalize text-gray-800">
            {formatMonthYear()}
          </h3>
          
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            <Button 
              variant={view === 'month' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setView('month')}
              className={view === 'month' ? 'bg-red-500 hover:bg-red-600' : ''}
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Mois
            </Button>
            <Button 
              variant={view === 'week' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setView('week')}
              className={view === 'week' ? 'bg-red-500 hover:bg-red-600' : ''}
            >
              <List className="w-4 h-4 mr-1" />
              Semaine
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Légende en haut */}
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg border">
              <span className="text-sm font-semibold text-gray-700">Légende :</span>
              {Object.entries(STATUS_LABELS).filter(([key]) => 
                !['in_progress', 'planned'].includes(key)
              ).map(([key, label]) => {
                const colors = STATUS_COLORS[key];
                return (
                  <div key={key} className="flex items-center gap-1.5">
                    <div className={cn("w-4 h-4 rounded-full shadow-sm", colors.bg)} />
                    <span className="text-sm text-gray-600">{label}</span>
                  </div>
                );
              })}
            </div>

            {/* Week day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2 bg-gray-100 rounded">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map(({ date, isCurrentMonth }, index) => {
                const dayEvents = getEventsForDay(date);
                const isToday = date.toDateString() === new Date().toDateString();
                const isSelected = selectedDay === date.toDateString();
                
                return (
                  <div 
                    key={index}
                    className={cn(
                      "min-h-[120px] p-1.5 border rounded-lg transition-all cursor-pointer hover:shadow-md",
                      isCurrentMonth ? "bg-white" : "bg-gray-50/50",
                      isToday && "ring-2 ring-red-500 ring-offset-1",
                      isSelected && "bg-red-50"
                    )}
                    onClick={() => setSelectedDay(isSelected ? null : date.toDateString())}
                  >
                    <div className={cn(
                      "text-sm font-bold mb-1.5 text-right pr-1",
                      isCurrentMonth ? "text-gray-900" : "text-gray-400",
                      isToday && "text-red-600"
                    )}>
                      {date.getDate()}
                    </div>
                    
                    {/* Events */}
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((event, idx) => (
                        <CalendarEvent
                          key={event.id || idx}
                          event={event}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event);
                          }}
                          compact={dayEvents.length > 2}
                        />
                      ))}
                      {dayEvents.length > 2 && (
                        <div 
                          className="text-xs font-semibold text-red-600 text-center py-1 bg-red-50 rounded-md cursor-pointer hover:bg-red-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDay(date.toDateString());
                          }}
                        >
                          +{dayEvents.length - 2} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Selected day detail panel */}
            {selectedDay && (
              <div className="mt-4 p-4 bg-white rounded-lg border-2 border-red-200 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-lg text-gray-800">
                    {new Date(selectedDay).toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </h4>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDay(null)}>
                    Fermer
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {getEventsForDay(new Date(selectedDay)).length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Aucun transfert ce jour</p>
                  ) : (
                    getEventsForDay(new Date(selectedDay)).map((event, idx) => {
                      const colors = STATUS_COLORS[event.status] || STATUS_COLORS.planned;
                      return (
                        <div
                          key={event.id || idx}
                          className={cn(
                            "p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all",
                            colors.bgLight
                          )}
                          onClick={() => handleEventClick(event)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={cn("w-3 h-3 rounded-full", colors.bg)} />
                              <span className="font-semibold text-gray-800">
                                {event.clientName || event.client || 'Client'}
                              </span>
                            </div>
                            <Badge className={cn(colors.bg, colors.text, "text-xs")}>
                              {STATUS_LABELS[event.status] || getCentralStatusLabel(event.status)}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            <span>{event.departure || 'N/A'}</span>
                            <span className="text-gray-400">→</span>
                            <span>{event.arrival || 'N/A'}</span>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Truck className="w-3 h-3" />
                              {event.title || 'N/A'}
                            </span>
                            {event.weight && (
                              <span>{event.weight} T</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            
            {/* Summary Stats */}
            <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                <div className="p-2">
                  <p className="text-3xl font-bold text-gray-900">{events.length}</p>
                  <p className="text-sm text-gray-500 font-medium">Total</p>
                </div>
                <div className="p-2">
                  <p className="text-3xl font-bold text-slate-600">
                    {events.filter(e => ['planned', 'planifiee'].includes(e.status)).length}
                  </p>
                  <p className="text-sm text-gray-500 font-medium">Planifiés</p>
                </div>
                <div className="p-2">
                  <p className="text-3xl font-bold text-amber-500">
                    {events.filter(e => ['en_cours_de_chargement', 'en_transit', 'en_cours_de_dechargement', 'in_progress'].includes(e.status)).length}
                  </p>
                  <p className="text-sm text-gray-500 font-medium">En cours</p>
                </div>
                <div className="p-2">
                  <p className="text-3xl font-bold text-emerald-600">
                    {events.filter(e => e.status === 'completed').length}
                  </p>
                  <p className="text-sm text-gray-500 font-medium">Terminés</p>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TransferCalendar;
